/**
 * useDeployMode hook — Phase 2a-tenant-signup-frontend FE-W-01.
 *
 * 동작:
 *   - 마운트 시 `/api/v1/system/mode` 조회 → mode 캐시
 *   - 5분 cache (TTL) — 같은 hook 두 번 mount 시 fetch 1회만
 *   - fail-closed: 에러 시 isSaaS=false (회사 가입 버튼 비가시 — 권한 누출 방지)
 *
 * Cache 정책 (Auditor A-4):
 *   - module-level singleton (localStorage 0 — XSS surface 회피)
 *   - process 단위 — 페이지 새로고침 시 reset (5분 TTL 정합)
 *
 * 반환:
 *   - { mode, isSaaS, isLoading, error }
 *   - mode: 'saas' | 'per_tenant' | null
 *   - isSaaS: mode === 'saas' (boolean, render branch 용)
 *   - isLoading: 초기 fetch 진행 중 + cache miss
 *   - error: 마지막 fetch 에러 (null 이면 정상)
 */

import { useEffect, useState } from 'react';
import { getDeployMode, type DeployMode } from '../api/system';

interface CachedMode {
  mode: DeployMode;
  fetchedAt: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
let cached: CachedMode | null = null;
let inflight: Promise<DeployMode> | null = null;

/** Test 전용 — module cache reset. */
export function _resetDeployModeCacheForTests(): void {
  cached = null;
  inflight = null;
}

function readCache(now: number): DeployMode | null {
  if (!cached) return null;
  if (now - cached.fetchedAt > CACHE_TTL_MS) {
    cached = null;
    return null;
  }
  return cached.mode;
}

async function fetchDeployMode(): Promise<DeployMode> {
  if (inflight) return inflight;
  inflight = (async () => {
    const mode = await getDeployMode();
    cached = { mode, fetchedAt: Date.now() };
    return mode;
  })()
    .catch((err) => {
      // fail-closed — cache 안 함, 다음 시도에서 재시도
      throw err;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export interface UseDeployModeResult {
  mode: DeployMode | null;
  isSaaS: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useDeployMode(): UseDeployModeResult {
  const initial = readCache(Date.now());
  const [mode, setMode] = useState<DeployMode | null>(initial);
  const [isLoading, setIsLoading] = useState<boolean>(initial === null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (initial !== null) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchDeployMode()
      .then((m) => {
        if (cancelled) return;
        setMode(m);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setMode(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    mode,
    isSaaS: mode === 'saas',
    isLoading,
    error,
  };
}
